import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

/**
 * A row of mutually exclusive options in a pill-shaped track (e.g. Last 10 / YTD / All).
 * Presentational only: the parent owns the selected value.
 */
const Track = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  border: 1px solid ${(props) => props.theme.colors.borderMedium};
  border-radius: 999px;
  background: ${(props) => props.theme.colors.surface};
`;

const Segment = styled('button', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})`
  padding: 6px 16px;
  border: none;
  border-radius: 999px;
  background: ${(props) => (props.isSelected ? props.theme.colors.surfaceHoverStrong : 'transparent')};
  font-family: "Nunito Sans";
  font-size: 14px;
  font-weight: ${(props) => (props.isSelected ? 700 : 600)};
  color: ${(props) => (props.isSelected ? props.theme.colors.text : props.theme.colors.textSecondary)};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  &:hover {
    color: ${(props) => props.theme.colors.text};
  }
`;

const SegmentedControl = ({
  options, value, onChange, ariaLabel,
}) => (
  <Track role='group' aria-label={ariaLabel}>
    {options.map((option) => (
      <Segment
        key={option.id}
        type='button'
        isSelected={option.id === value}
        aria-pressed={option.id === value}
        onClick={() => onChange(option.id)}
      >
        {option.label}
      </Segment>
    ))}
  </Track>
);

SegmentedControl.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string,
};

SegmentedControl.defaultProps = {
  ariaLabel: undefined,
};

export default SegmentedControl;
